/**
 * Minecart wiring (DOM-free, so the smoke tests drive it): what a click on a rail or on a cart does, the
 * rider seat, and the per-tick glue between the player and the sim.
 *
 *  - right-click a rail holding a minecart: place a cart on it (survival consumes one);
 *  - right-click a cart: mount it (shared mount model, game/riding.ts); Shift dismounts;
 *  - riding, W nudges the cart along the track the way you look (vanilla: the rider's own walking pushes a slow cart);
 *  - punching a cart hurts it (5 bare-handed hits, one in creative): it breaks, drops the minecart item and drops the rider.
 *
 * main.ts: `minecarts.use(...)` early in the right-click handler, `minecarts.punch(...)` in the left-click one,
 * `minecarts.tick()` once per game tick, `renderer.update(...)` per frame, `serialize/load` in the stats blob.
 */

import { RAIL_SHAPE } from '../../blocks';
import { I } from '../../game/itemIds';
import { dismountPlayer, mountPlayer, ride, type Rideable } from '../../game/riding';
import { isEmpty, stackOf, type Stack } from '../../game/types';
import { railHeight, Minecart, MinecartSim } from './minecart';
import { EXITS } from '../../world/rails';

/** The slice of Player the cart needs. */
export interface CartPlayer {
  yaw: number;
  sitting: boolean;
  rideTick: (() => void) | null;
  rideInput(out: { fwd: number; strafe: number; jump: boolean; jumpTapped: boolean; sneak: boolean }): void;
  rideTo(x: number, y: number, z: number, snap?: boolean): void;
  setBodyYaw(y: number): void;
  resetFall(): void;
}

export interface MinecartEnv {
  getBlock(x: number, y: number, z: number): number;
  dimension(): string;
  creative(): boolean;
  player: CartPlayer;
  /** Spawn a dropped item stack. */
  drop(stack: Stack, x: number, y: number, z: number): void;
  consumeHeld(n: number): void;
  /** Player died or is travelling between dimensions: get off. */
  mustDismount?(): boolean;
  toast?(msg: string): void;
}

export class MinecartSystem {
  readonly sim = new MinecartSim();
  private rider: Minecart | null = null;
  private age = 0;
  private readonly input = { fwd: 0, strafe: 0, jump: false, jumpTapped: false, sneak: false };
  private readonly tmp = { x: 0, y: 0, z: 0 };
  private bodyYaw = 0;

  constructor(private readonly env: MinecartEnv) {
    // compose with whatever else installed a ride hook (mounts.ts installs its own; the boat may too)
    const p = env.player;
    const prev = p.rideTick;
    p.rideTick = () => {
      if (this.rider) this.riderTick();
      else prev?.();
    };
  }

  get riding(): Minecart | null { return this.rider; }

  /** Right-click: `held` is the held stack, `hit` the targeted block (or null), `blockDist` how far the block face is. */
  use(held: Stack, hit: { x: number; y: number; z: number; id: number } | null, eye: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, blockDist: number): boolean {
    const dim = this.env.dimension();
    if (!ride.current) {
      const t = this.sim.raycast(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, 4.5, dim);
      if (t && t.dist < blockDist) return this.mount(t.cart);
    }
    if (hit && !isEmpty(held) && held.id === I.MINECART && RAIL_SHAPE[hit.id] >= 0) {
      this.place(hit.x, hit.y, hit.z, hit.id);
      if (!this.env.creative()) this.env.consumeHeld(1);
      return true;
    }
    return false;
  }

  /** Put a cart on the rail block at (x, y, z). */
  place(x: number, y: number, z: number, railId: number): Minecart {
    const shape = RAIL_SHAPE[railId];
    const [a, b] = EXITS[shape];
    const heading = Math.atan2(b[2] - a[2], b[0] - a[0]);
    return this.sim.spawn(x + 0.5, y + railHeight(shape, 0.5, 0.5), z + 0.5, heading, this.env.dimension());
  }

  mount(cart: Minecart): boolean {
    if (this.rider || ride.current || !mountPlayer(cart)) return false;
    this.rider = cart;
    this.age = 0;
    const p = this.env.player;
    p.sitting = true;
    this.bodyYaw = cart.yaw;
    this.seat(true);
    this.env.toast?.('Press Shift to dismount');
    return true;
  }

  dismount(): void {
    const cart = this.rider;
    if (!cart) return;
    this.rider = null;
    const v: Rideable | null = dismountPlayer();
    const p = this.env.player;
    p.sitting = false;
    cart.pushX = cart.pushZ = 0;
    if (v) { cart.dismountPos(this.tmp, { getBlock: this.env.getBlock }); p.rideTo(this.tmp.x, this.tmp.y, this.tmp.z, true); }
    p.resetFall();
  }

  /** Left-click: true when the click hit a cart (not hidden by a nearer block). `damage` is the held weapon's attack damage. */
  punch(eye: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, blockDist: number, damage = 1): boolean {
    const t = this.sim.raycast(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, 4.5, this.env.dimension());
    if (!t || t.dist >= blockDist) return false;
    if (t.cart.hurt(damage, this.env.creative())) this.breakCart(t.cart);
    return true;
  }

  /** The cart is destroyed: the rider gets out and the item drops (creative drops nothing). */
  breakCart(cart: Minecart): void {
    if (this.rider === cart) this.dismount();
    cart.removed = true;
    if (!this.env.creative()) this.env.drop(stackOf(I.MINECART), cart.x, cart.y + 0.3, cart.z);
  }

  /** Once per game tick (after the player's own tick, which runs `riderTick`). */
  tick(): void {
    if (this.rider && ride.current !== this.rider) { // someone else (mounts.dismountRider on death / portal) already got the rider off
      this.rider.pushX = this.rider.pushZ = 0;
      this.rider = null;
      this.env.player.sitting = false;
    }
    const cart = this.rider;
    if (cart && (cart.removed || this.env.mustDismount?.())) this.dismount();
    this.sim.tick({ getBlock: this.env.getBlock }, this.env.dimension());
    if (this.rider) this.seat(false);
  }

  /** The player's tick while seated (Player.rideTick): input to the cart, Shift gets off. */
  private riderTick(): void {
    const cart = this.rider, p = this.env.player;
    if (!cart) return;
    p.rideInput(this.input);
    if ((this.input.sneak && ++this.age > 2) || cart.removed) { this.dismount(); return; }
    if (!this.input.sneak) this.age = Math.max(this.age, 3);
    cart.steer({ forward: this.input.fwd, strafe: this.input.strafe, jump: this.input.jump, sneak: this.input.sneak });
    // walking pushes: the look direction scaled like a walking step (vanilla's rider deltaMovement)
    const f = this.input.fwd;
    cart.pushX = f > 0 ? -Math.sin(p.yaw) * 0.15 : 0;
    cart.pushZ = f > 0 ? -Math.cos(p.yaw) * 0.15 : 0;
  }

  private seat(snap: boolean): void {
    const cart = this.rider;
    if (!cart) return;
    cart.seatPos(0, this.tmp);
    const p = this.env.player;
    p.rideTo(this.tmp.x, this.tmp.y, this.tmp.z, snap);
    // face along the track (player yaw 0 looks down -z: forward is (-sin, -cos))
    if (cart.speed > 0.01 || snap) this.bodyYaw = Math.atan2(-Math.cos(cart.yaw), -Math.sin(cart.yaw));
    p.setBodyYaw(this.bodyYaw);
  }

  serialize(): number[][] { return this.sim.serialize(); }
  load(data: unknown): void { this.sim.load(data); }
}
